/**
 * Helpers for the three single-select radio filter groups on the Positions list
 * (ER-10494 / MDP-551) — Validity, Occupied, Preparing for.
 *
 * The Positions applied-filter token space is the human LABEL (the same tokens the
 * legacy SearchModal persists and the merged grid code already maps to API params).
 * resolveMdp551PositionApiFilters() in the view state turns those labels into the
 * ER-10493 backend params (validity=valid|not_valid, occupied=occupied|not_occupied,
 * preparing_for=has_preparing|no_preparing), so the Filter Modal Standard groups stay
 * label-based end-to-end:
 *
 *   - validity:      "" | "Is valid" | "Is not valid"
 *   - occupied:      "" | "Is occupied" | "Vacant"   (ER-11535: renamed from "Is not occupied")
 *   - preparingFor:  "" | "Has Preparing for" | "Nobody prepares for"
 *   - rolesAttached: "" | "Attached" | "No role attached"   (ER-10517 / MDP-558)
 */

/**
 * ER-11535: "Is not occupied" was renamed to "Vacant" to match the Vacant KPI card.
 * Filters persisted before the rename still carry the old label, so the payload
 * builder normalizes the legacy token to the new canonical label. This keeps the
 * modal's radio selected-state correct without migrating the persisted storage.
 */
export const LEGACY_POSITION_RADIO_VALUE_ALIASES = Object.freeze({
  "Is not occupied": "Vacant",
});

/**
 * ER-11530: applied-filter chip labels that differ from the persisted filter token, so the
 * chip a summary-tile click produces reads like the tile ("Invalid"), not the filter name.
 */
export const POSITION_FILTER_CHIP_LABELS = Object.freeze({
  "Is not valid": "Invalid",
});

export function getPositionFilterChipLabel(token) {
  return POSITION_FILTER_CHIP_LABELS[token] || token;
}

export function getPositionFilterTokenForChipLabel(chipLabel) {
  const entry = Object.entries(POSITION_FILTER_CHIP_LABELS).find(
    ([, label]) => label === chipLabel
  );
  return entry ? entry[0] : chipLabel;
}

export const DEFAULT_POSITION_RADIO_FILTER_PAYLOAD = Object.freeze({
  validity: "",
  occupied: "",
  preparingFor: "",
  rolesAttached: "",
});

/** Group definitions used by FilterPositionsModal and payload conversion. */
export const POSITION_RADIO_GROUPS = Object.freeze([
  {
    key: "validity",
    label: "Validity",
    items: [
      { label: "No Filter", value: "" },
      { label: "Is valid", value: "Is valid" },
      { label: "Is not valid", value: "Is not valid" },
    ],
  },
  {
    key: "occupied",
    label: "Occupied",
    items: [
      { label: "No Filter", value: "" },
      { label: "Is occupied", value: "Is occupied" },
      { label: "Vacant", value: "Vacant" },
    ],
  },
  {
    key: "preparingFor",
    label: "Preparing for",
    items: [
      { label: "No Filter", value: "" },
      { label: "Has Preparing for", value: "Has Preparing for" },
      { label: "Nobody prepares for", value: "Nobody prepares for" },
    ],
  },
  {
    // ER-10517 / MDP-558 — net-new, not tied to an indicator card.
    key: "rolesAttached",
    label: "Roles-attached",
    items: [
      { label: "No Filter", value: "" },
      { label: "Attached", value: "Attached" },
      { label: "No role attached", value: "No role attached" },
    ],
  },
]);

/** Applied-filter array -> { validity, occupied, preparingFor } labels ("" default). */
export function buildPositionRadioFilterPayload(filters = []) {
  const values = (Array.isArray(filters) ? filters : [])
    .map((value) => String(value))
    .map((value) => LEGACY_POSITION_RADIO_VALUE_ALIASES[value] || value);
  const payload = { ...DEFAULT_POSITION_RADIO_FILTER_PAYLOAD };

  POSITION_RADIO_GROUPS.forEach((group) => {
    const item = group.items.find((option) => option.value && values.includes(option.value));
    if (item) payload[group.key] = item.value;
  });

  return payload;
}

/** Payload -> applied-filter array (only non-default labels). */
export function payloadToPositionRadioFilterValues(payload = {}) {
  const values = [];
  POSITION_RADIO_GROUPS.forEach((group) => {
    const value = payload?.[group.key];
    if (value) values.push(value);
  });
  return values;
}
